import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { Suspense, lazy } from 'react';
import { AuthProvider } from '@contexts/AuthContext';
import { CartProvider } from '@contexts/CartContext';
import ProtectedRoute from '@components/auth/ProtectedRoute';
import LoadingSpinner from '@components/common/LoadingSpinner';
import { UserRole } from '@/types/user.types';

// Lazy load pages for code splitting
const LoginPage = lazy(() => import('@pages/auth/LoginPage'));
const DashboardPage = lazy(() => import('@pages/dashboard/DashboardPage'));
const ProductsPage = lazy(() => import('@pages/products/ProductsPage'));
const OrdersPage = lazy(() => import('@pages/orders/OrdersPage'));
const ProfilePage = lazy(() => import('@pages/profile/ProfilePage'));
const UnauthorizedPage = lazy(() => import('@pages/errors/UnauthorizedPage'));
const NotFoundPage = lazy(() => import('@pages/errors/NotFoundPage'));

// Admin pages
const UserManagementPage = lazy(() => import('@pages/admin/UserManagementPage'));
const ProductUploadPage = lazy(() => import('@pages/admin/ProductUploadPage'));
const AssignProductsPage = lazy(() => import('@pages/admin/AssignProductsPage'));
const SettingsPage = lazy(() => import('@pages/admin/SettingsPage'));
const QRCodeManagementPage = lazy(() => import('@pages/admin/QRCodeManagementPage'));
const TransactionLogPage = lazy(() => import('@pages/admin/TransactionLogPage'));

// QR pages (public)
const QRScanPage = lazy(() => import('@pages/qr/QRScanPage'));
const QROrderPage = lazy(() => import('@pages/qr/QROrderPage'));
const QRSupportPage = lazy(() => import('@pages/qr/QRSupportPage'));

// MUI Theme
const theme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: '#1976d2',
    },
    secondary: {
      main: '#dc004e',
    },
    background: {
      default: '#f5f5f5',
    },
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
        },
      },
    },
  },
});

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AuthProvider>
        <CartProvider>
          <Router>
            <Suspense fallback={<LoadingSpinner fullScreen />}>
            <Routes>
              {/* Public routes */}
              <Route path="/login" element={<LoginPage />} />
              <Route path="/qr/:qrCodeId" element={<QRScanPage />} />
              <Route path="/qr/:qrCodeId/order" element={<QROrderPage />} />
              <Route path="/qr/:qrCodeId/support" element={<QRSupportPage />} />

              {/* Protected routes - All authenticated users */}
              <Route
                path="/dashboard"
                element={
                  <ProtectedRoute>
                    <DashboardPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/products"
                element={
                  <ProtectedRoute>
                    <ProductsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/orders"
                element={
                  <ProtectedRoute>
                    <OrdersPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/profile"
                element={
                  <ProtectedRoute>
                    <ProfilePage />
                  </ProtectedRoute>
                }
              />

              {/* Admin routes */}
              <Route
                path="/admin/users"
                element={
                  <ProtectedRoute requiredRole={UserRole.ADMIN}>
                    <UserManagementPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/upload"
                element={
                  <ProtectedRoute requiredRole={UserRole.ADMIN}>
                    <ProductUploadPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/assign-products"
                element={
                  <ProtectedRoute requiredRole={UserRole.ADMIN}>
                    <AssignProductsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/qr-codes"
                element={
                  <ProtectedRoute requiredRole={UserRole.ADMIN}>
                    <QRCodeManagementPage />
                  </ProtectedRoute>
                }
              />

              {/* Super Admin routes */}
              <Route
                path="/admin/settings"
                element={
                  <ProtectedRoute requiredRole={UserRole.SUPER_ADMIN}>
                    <SettingsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/transactions"
                element={
                  <ProtectedRoute requiredRole={UserRole.SUPER_ADMIN}>
                    <TransactionLogPage />
                  </ProtectedRoute>
                }
              />

              {/* Error routes */}
              <Route path="/unauthorized" element={<UnauthorizedPage />} />
              <Route path="/404" element={<NotFoundPage />} />

              {/* Redirects */}
              <Route path="/" element={<Navigate to="/dashboard" replace />} />
              <Route path="*" element={<Navigate to="/404" replace />} />
            </Routes>
            </Suspense>
          </Router>
        </CartProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
